import { useEffect, useRef, useState } from "react";
import { CheckCircle2, AlertTriangle, Loader2, Sparkles, ThumbsUp, ThumbsDown, Globe } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const jhdr = { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" };
const jfetch = async (p, opts = {}) => {
  const r = await fetch(`${API}${p}`, { credentials: "include", ...opts });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || "Request failed");
  return d;
};
const defaultInput = "bg-gf-bg border border-white/15 px-3 py-2 font-body text-xs text-gf-text focus:outline-none focus:border-gf-primary w-full";
let _opts = null, _optsP = null;
export const useSmartOptions = () => {
  const [o, setO] = useState(_opts);
  useEffect(() => { if (_opts) return; _optsP = _optsP || jfetch("/smart/options").then((d) => { _opts = d; return d; }); _optsP.then(setO).catch(() => {}); }, []);
  return o || { states: [], industries: [], sizes: [], cities: [], entity_types: [] };
};

export const sendSmartFeedback = (kind, key, rating, corrections = {}) => jfetch("/smart/feedback", { method: "POST", headers: jhdr, body: JSON.stringify({ kind, key, rating, corrections }) });

const Status = ({ tone, children, testid }) => {
  const c = { ok: "text-emerald-400", warn: "text-amber-400", bad: "text-red-400", muted: "text-gf-muted" }[tone];
  return <p className={`font-body text-[10px] mt-1 inline-flex items-center gap-1 flex-wrap ${c}`} data-testid={testid}>{children}</p>;
};

/** GSTIN input with checksum validation, Vidura web lookup → onResolved({legal_name, pan, state, ...}), and 👍/👎 feedback loop. */
export const GstinField = ({ value, onChange, onResolved, className = defaultInput, placeholder = "GSTIN", testid = "smart-gstin", autoFocus }) => {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [fix, setFix] = useState("");
  const [fb, setFb] = useState(null);
  const last = useRef("");
  const lookup = async (g, force = false) => {
    if (g.length !== 15 || (g === last.current && !force)) return;
    last.current = g; setBusy(true); setFb(null); setFixing(false);
    try {
      const d = await jfetch(`/smart/gstin/${g}${force ? "?force=true" : ""}`);
      setRes(d);
      if (d.valid && onResolved) onResolved(d);
    } catch (e) { setRes({ valid: true, found: false, error: e.message }); } finally { setBusy(false); }
  };
  useEffect(() => { const g = (value || "").replace(/\s/g, "").toUpperCase(); if (g.length === 15) lookup(g); else if (g.length < 15 && res) { setRes(null); last.current = ""; } }, [value]); // eslint-disable-line
  const rate = async (rating) => {
    try { await sendSmartFeedback("gstin", value, rating, rating === "down" && fix ? { legal_name: fix } : {}); setFb(rating); if (rating === "down" && fix && onResolved) onResolved({ ...res, legal_name: fix }); toast.success(rating === "up" ? "Thanks — Vidura noted this match as correct" : fix ? "Correction saved — future lookups use your name" : "Noted — lookup will be re-run next time"); setFixing(false); } catch (e) { toast.error(e.message); }
  };
  return (
    <div data-testid={`${testid}-wrap`}>
      <div className="relative">
        <input className={className} placeholder={placeholder} value={value || ""} maxLength={15} autoFocus={autoFocus} onChange={(e) => onChange(e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, ""))} data-testid={testid} />
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-gf-primary absolute right-2 top-2.5" />}
      </div>
      {busy && <Status tone="muted" testid={`${testid}-status`}><Sparkles className="w-3 h-3" /> Vidura is verifying this GSTIN…</Status>}
      {!busy && res && !res.valid && <Status tone="bad" testid={`${testid}-status`}><AlertTriangle className="w-3 h-3" /> {res.reason}</Status>}
      {!busy && res && res.valid && (
        <div>
          <Status tone={res.found ? "ok" : "warn"} testid={`${testid}-status`}>
            {res.found ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
            {res.found ? <><b>{res.legal_name}</b>{res.trade_name && res.trade_name !== res.legal_name ? ` (${res.trade_name})` : ""} · </> : "Valid GSTIN — name not found online; "}
            {res.state} · {res.entity_type_short} · PAN {res.pan}{res.verified_by_team ? " · verified by team" : res.found && res.confidence ? ` · ${res.confidence}% match` : ""}
          </Status>
          {res.found && fb !== "up" && !fixing && (
            <span className="inline-flex items-center gap-1 ml-2">
              <button type="button" onClick={() => rate("up")} className="text-gf-muted hover:text-emerald-400" title="Correct match" data-testid={`${testid}-fb-up`}><ThumbsUp className="w-3 h-3" /></button>
              <button type="button" onClick={() => { setFixing(true); setFix(res.legal_name || ""); }} className="text-gf-muted hover:text-orange-400" title="Wrong name — correct it" data-testid={`${testid}-fb-down`}><ThumbsDown className="w-3 h-3" /></button>
            </span>
          )}
          {!res.found && !res.error && <button type="button" onClick={() => lookup(value, true)} className="font-mono text-[9px] uppercase text-gf-primary ml-2" data-testid={`${testid}-retry`}>Retry lookup</button>}
          {fixing && (
            <div className="flex gap-1 mt-1" data-testid={`${testid}-fix`}>
              <input className={`${className} !py-1`} value={fix} onChange={(e) => setFix(e.target.value)} placeholder="Correct legal name" data-testid={`${testid}-fix-input`} />
              <button type="button" onClick={() => rate("down")} className="bg-gf-primary text-gf-bg font-body font-bold text-[10px] px-2" data-testid={`${testid}-fix-save`}>Teach Vidura</button>
            </div>
          )}
          {fb === "up" && <span className="font-mono text-[9px] text-gf-primary ml-2">✓ learned</span>}
        </div>
      )}
    </div>
  );
};

export const PanField = ({ value, onChange, className = defaultInput, placeholder = "PAN", testid = "smart-pan" }) => {
  const v = (value || "").toUpperCase();
  const ok = /^[A-Z]{3}[ABCFGHLJPT][A-Z][0-9]{4}[A-Z]$/.test(v);
  const ent = { C: "Company", P: "Individual / Proprietor", H: "HUF", F: "Firm / LLP", A: "AOP", T: "Trust", B: "BOI", L: "Local authority", J: "Juridical person", G: "Government" }[v[3]];
  return (
    <div>
      <input className={className} placeholder={placeholder} value={v} maxLength={10} onChange={(e) => onChange(e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, ""))} data-testid={testid} />
      {v.length === 10 && (ok ? <Status tone="ok" testid={`${testid}-status`}><CheckCircle2 className="w-3 h-3" /> Valid PAN · {ent}</Status> : <Status tone="bad" testid={`${testid}-status`}><AlertTriangle className="w-3 h-3" /> Not a valid PAN pattern (AAAAA9999A)</Status>)}
    </div>
  );
};

/** Select with canonical options + "Other…" free text. */
export const SmartSelect = ({ value, onChange, options, placeholder = "Select…", className = defaultInput, testid = "smart-select", allowOther = true }) => {
  const known = options.includes(value);
  const [other, setOther] = useState(Boolean(value) && !known);
  useEffect(() => { if (value && !options.includes(value) && options.length) setOther(true); }, [value, options]);
  return (
    <div className="flex gap-1">
      <select className={className} value={other ? "__other" : value || ""} onChange={(e) => { if (e.target.value === "__other") { setOther(true); onChange(""); } else { setOther(false); onChange(e.target.value); } }} data-testid={testid}>
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
        {allowOther && <option value="__other">Other…</option>}
      </select>
      {other && <input className={className} placeholder="Type value" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-other`} />}
    </div>
  );
};

export const StateSelect = (p) => { const o = useSmartOptions(); return <SmartSelect {...p} options={o.states.map((s) => s.name)} placeholder="State / UT" allowOther={false} />; };
export const IndustrySelect = (p) => { const o = useSmartOptions(); return <SmartSelect {...p} options={o.industries} placeholder="Industry" />; };
export const SizeSelect = (p) => { const o = useSmartOptions(); return <SmartSelect {...p} options={o.sizes} placeholder="Company size (people)" allowOther={false} />; };
export const EntityTypeSelect = (p) => { const o = useSmartOptions(); return <SmartSelect {...p} options={o.entity_types} placeholder="Entity type" />; };
export const CityInput = ({ value, onChange, className = defaultInput, testid = "smart-city", placeholder = "City" }) => {
  const o = useSmartOptions();
  return (<>
    <input className={className} list="smart-cities" placeholder={placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    <datalist id="smart-cities">{o.cities.map((c) => <option key={c} value={c} />)}</datalist>
  </>);
};

const FREE_MAIL = /^(gmail|yahoo|hotmail|outlook|live|icloud|rediffmail|proton(mail)?|aol|zoho|yandex|me|msn)\./i;
/** Company name input that suggests the organisation from the work-email domain (via Vidura website profiling). */
export const CompanyInput = ({ value, onChange, email, className = defaultInput, placeholder = "Company", testid = "smart-company", required }) => {
  const [sugg, setSugg] = useState(null);
  const [busy, setBusy] = useState(false);
  const tried = useRef("");
  useEffect(() => {
    const dom = ((email || "").split("@")[1] || "").toLowerCase().trim();
    if (!dom || !dom.includes(".") || FREE_MAIL.test(dom) || tried.current === dom || value) return;
    const t = setTimeout(async () => {
      tried.current = dom; setBusy(true);
      try { const d = await jfetch("/smart/website", { method: "POST", headers: jhdr, body: JSON.stringify({ url: dom }) }); if (d.found && d.name) setSugg(d); } catch { /* silent */ } finally { setBusy(false); }
    }, 700);
    return () => clearTimeout(t);
  }, [email]); // eslint-disable-line
  return (
    <div>
      <div className="relative"><input required={required} placeholder={placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} className={className} data-testid={testid} />{busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-gf-primary absolute right-2 top-1/2 -translate-y-1/2" />}</div>
      {sugg && !value && <button type="button" onClick={() => { onChange(sugg.legal_name || sugg.name); setSugg(null); }} className="font-body text-[10px] text-gf-primary mt-1 inline-flex items-center gap-1" data-testid={`${testid}-suggest`}><Sparkles className="w-3 h-3" /> Use “{sugg.legal_name || sugg.name}”{sugg.industry ? ` · ${sugg.industry}` : ""}</button>}
    </div>
  );
};

/** Website URL input with "Profile with Vidura" → onResolved({name, legal_name, industry, city, state, size, entity_type}). */
export const WebsiteField = ({ value, onChange, onResolved, className = defaultInput, testid = "smart-website" }) => {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    if (!value) return; setBusy(true);
    try { const d = await jfetch("/smart/website", { method: "POST", headers: jhdr, body: JSON.stringify({ url: value }) }); setRes(d); if (d.found && onResolved) onResolved(d); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="flex gap-1">
        <input className={className} placeholder="https://company.com" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        <button type="button" onClick={run} disabled={busy || !value} title="Fill org details from the website" className="border border-gf-primary/40 text-gf-primary px-2 disabled:opacity-40 inline-flex items-center gap-1 font-mono text-[9px] uppercase whitespace-nowrap" data-testid={`${testid}-profile`}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Globe className="w-3 h-3" />} Profile</button>
      </div>
      {res && <Status tone={res.found ? "ok" : "warn"} testid={`${testid}-status`}>{res.found ? <><Sparkles className="w-3 h-3" /> {res.name}{res.industry ? ` · ${res.industry}` : ""}{res.city ? ` · ${res.city}` : ""} — {res.summary}</> : "Could not profile this site"}</Status>}
    </div>
  );
};
